// Downsample mixed meeting audio to mono s16le and measure peak (FR-M12).
// The extension offscreen mixer and the web preview both feed this.

export interface PcmChunk {
  seq: number;
  t_ms: number;
  sample_rate: number;
  channels: 1;
  pcm_s16le_b64: string;
  peak: number;
}

export function encodeS16leBase64(samples: Float32Array): { b64: string; peak: number } {
  const out = new Int16Array(samples.length);
  let peak = 0;
  for (let i = 0; i < samples.length; i++) {
    const x = Math.max(-1, Math.min(1, samples[i] ?? 0));
    peak = Math.max(peak, Math.abs(x));
    out[i] = x < 0 ? Math.round(x * 0x8000) : Math.round(x * 0x7fff);
  }
  const bytes = new Uint8Array(out.buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return { b64: btoa(binary), peak };
}

/** ScriptProcessor is deprecated but is the only sync tap that works in an
 * offscreen document without an AudioWorklet module URL. Buffer size 4096 at
 * 16 kHz ≈ 256 ms — several callbacks make one ~1 s POST (FR-M12: small). */
export function tapMono16k(
  ctx: AudioContext,
  source: AudioNode,
  onFrame: (samples: Float32Array) => void,
): { stop: () => void; pause: () => void; resume: () => void } {
  const processor = ctx.createScriptProcessor(4096, 1, 1);
  processor.onaudioprocess = (ev) => {
    const input = ev.inputBuffer.getChannelData(0);
    onFrame(new Float32Array(input));
  };
  let connected = true;
  source.connect(processor);
  // Keep the node alive without audible output.
  const mute = ctx.createGain();
  mute.gain.value = 0;
  processor.connect(mute);
  mute.connect(ctx.destination);
  return {
    stop: () => {
      processor.disconnect();
      mute.disconnect();
      if (connected) source.disconnect(processor);
      connected = false;
    },
    // F17 / PR5 carry-over: pausing the recording pauses the tap too, so no audio reaches
    // live ASR while paused. The processor stays alive; only its input is cut.
    pause: () => {
      if (!connected) return;
      source.disconnect(processor);
      connected = false;
    },
    resume: () => {
      if (connected) return;
      source.connect(processor);
      connected = true;
    },
  };
}

export class ChunkAssembler {
  private pending: Float32Array[] = [];
  private pendingSamples = 0;
  private seq = 0;
  private readonly startedAt = Date.now();
  readonly targetRate = 16_000;
  readonly samplesPerChunk: number;

  constructor(seconds = 1) {
    this.samplesPerChunk = this.targetRate * seconds;
  }

  push(frame: Float32Array, sourceRate: number): PcmChunk | null {
    const resampled = sourceRate === this.targetRate ? frame : downsample(frame, sourceRate, this.targetRate);
    this.pending.push(resampled);
    this.pendingSamples += resampled.length;
    if (this.pendingSamples < this.samplesPerChunk) return null;
    const merged = new Float32Array(this.pendingSamples);
    let offset = 0;
    for (const part of this.pending) {
      merged.set(part, offset);
      offset += part.length;
    }
    this.pending = [];
    this.pendingSamples = 0;
    const slice = merged.subarray(0, this.samplesPerChunk);
    const leftover = merged.subarray(this.samplesPerChunk);
    if (leftover.length) {
      this.pending.push(leftover);
      this.pendingSamples = leftover.length;
    }
    const { b64, peak } = encodeS16leBase64(slice);
    this.seq += 1;
    return {
      seq: this.seq,
      t_ms: Date.now() - this.startedAt,
      sample_rate: this.targetRate,
      channels: 1,
      pcm_s16le_b64: b64,
      peak,
    };
  }
}

export function downsample(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const length = Math.max(1, Math.floor(input.length / ratio));
  const out = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    out[i] = input[Math.min(input.length - 1, Math.floor(i * ratio))] ?? 0;
  }
  return out;
}

/** FR-M12: if the main thread is falling behind, tell the caller to pause ASR
 * before the meeting stutters. Thresholds are conservative. */
export function shouldDegradeAsr(stats: { audioCallbackLagMs: number; postedQueue: number }): "cpu" | "bandwidth" | null {
  if (stats.audioCallbackLagMs > 250) return "cpu";
  if (stats.postedQueue > 8) return "bandwidth";
  return null;
}
